import { useQuery } from "@tanstack/react-query";
import { usePublicClient } from "wagmi";
import { STAKE_ABI } from "@/contract/abi/stake";
import ContractAddress from "@/contract/addresses";

/** One day in seconds — the spot-rate window for the daily reward. */
const ONE_DAY_SECONDS = 86_400n;

export type CalculatorRewardArgs = {
  /** NFT plan tier (1..5). */
  plan: number;
  /** DNM amount in wei (ether-scale). */
  dnmWei: bigint;
  /** Stake start time in Unix seconds. */
  startSec: bigint;
  /** Stake duration in seconds (12 / 18 / 24 month constant). */
  durationSec: bigint;
};

export type CalculatorReward = {
  /** Whole-term reward in wei (per-year decay already baked in by contract). */
  totalWei: bigint;
  /**
   * Spot reward for a single day at `startSec`, in wei. This mirrors the stake
   * page's "پاداش روزانه من" (slot-2 of its multicall) so the calculator and the
   * stake page agree. It is NOT `totalWei / days` — the contract decays reward
   * 20%/DAO-year, so the term average is lower than today's spot rate.
   */
  dailyWei: bigint;
};

export type CalculatorRewardResult = {
  data: CalculatorReward | undefined;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
};

/**
 * Reads StakeMeta.calculateReward for a calculator preview, in a single
 * multicall round-trip:
 *   - full-term total: calculateReward(plan, dnm, start, start + duration, duration)
 *   - 1-day spot:      calculateReward(plan, dnm, start, start + 86400,    duration)
 *
 * staleTime: 0 — every key change yields a fresh contract read.
 * retry: false — UI surfaces retry explicitly via refetch().
 */
export function useCalculatorReward(
  args: CalculatorRewardArgs | null,
): CalculatorRewardResult {
  const publicClient = usePublicClient();
  const enabled = args !== null && publicClient !== undefined;

  const query = useQuery<CalculatorReward>({
    queryKey: [
      "stake",
      "calculator-reward",
      args?.plan ?? null,
      args?.dnmWei.toString() ?? null,
      args?.startSec.toString() ?? null,
      args?.durationSec.toString() ?? null,
    ],
    enabled,
    staleTime: 0,
    retry: false,
    queryFn: async (): Promise<CalculatorReward> => {
      if (!publicClient || !args) throw new Error("Calculator reward disabled");
      const termEndSec = args.startSec + args.durationSec;
      const dayEndSec = args.startSec + ONE_DAY_SECONDS;
      const [totalWei, dailyWei] = await publicClient.multicall({
        contracts: [
          {
            address: ContractAddress.STAKE,
            abi: STAKE_ABI,
            functionName: "calculateReward",
            args: [
              args.plan,
              args.dnmWei,
              args.startSec,
              termEndSec,
              args.durationSec,
            ],
          },
          {
            address: ContractAddress.STAKE,
            abi: STAKE_ABI,
            functionName: "calculateReward",
            args: [
              args.plan,
              args.dnmWei,
              args.startSec,
              dayEndSec,
              args.durationSec,
            ],
          },
        ],
        allowFailure: false,
      });
      return { totalWei, dailyWei };
    },
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => {
      void query.refetch();
    },
  };
}
